export function createPreviewBridgeLifecycle(params: {
  install: () => void;
  onUnavailable: () => void;
  maxAttempts?: number;
  acknowledgementTimeoutMs?: number;
  schedule?: typeof setTimeout;
  cancel?: typeof clearTimeout;
}) {
  // CodeSandbox only guarantees that injection is available after the Preview
  // reaches CONNECTED; larger Astro pages can still take several seconds to
  // execute injected code after that event. Keep retrying without declaring
  // the inspector unavailable during a normal page/HMR startup.
  const maxAttempts = params.maxAttempts ?? 3;
  const acknowledgementTimeoutMs = params.acknowledgementTimeoutMs ?? 5_000;
  const schedule = params.schedule ?? setTimeout;
  const cancel = params.cancel ?? clearTimeout;
  let installTimer: ReturnType<typeof setTimeout> | undefined;
  let acknowledgementTimer: ReturnType<typeof setTimeout> | undefined;
  let attempts = 0;
  let connected = false;
  let acknowledged = false;
  let disposed = false;

  const clearTimers = () => {
    if (installTimer !== undefined) cancel(installTimer);
    if (acknowledgementTimer !== undefined) cancel(acknowledgementTimer);
    installTimer = undefined;
    acknowledgementTimer = undefined;
  };
  const install = () => {
    installTimer = undefined;
    if (disposed || !connected || acknowledged) return;
    attempts += 1;
    params.install();
    acknowledgementTimer = schedule(() => {
      acknowledgementTimer = undefined;
      if (disposed || !connected || acknowledged) return;
      if (attempts >= maxAttempts) {
        params.onUnavailable();
        return;
      }
      installTimer = schedule(install, 0);
    }, acknowledgementTimeoutMs);
  };

  return {
    connected() {
      clearTimers();
      attempts = 0;
      connected = true;
      acknowledged = false;
      // Queue behind CodeSandbox's own navigation-protocol injection.
      installTimer = schedule(install, 0);
    },
    acknowledged() {
      acknowledged = true;
      if (acknowledgementTimer !== undefined) cancel(acknowledgementTimer);
      acknowledgementTimer = undefined;
    },
    disconnected() {
      connected = false;
      acknowledged = false;
      clearTimers();
    },
    dispose() {
      disposed = true;
      connected = false;
      clearTimers();
    },
  };
}
